import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Instagram,
  Hash,
  Users,
  Settings,
  BarChart2,
  Plus,
  Edit2,
  Trash2,
  Download,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Zap,
  ToggleLeft,
  ToggleRight,
  X,
  Globe,
  Link,
  MessageSquare,
  UserCheck,
  AlertCircle,
  ChevronRight,
  ExternalLink,
  ShieldCheck,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ig-funnel`;
const ADMIN_HEADERS = {
  'X-Admin-Key': 'ruonn-admin-key-2024',
  'Content-Type': 'application/json',
};

// ─────────── Small reusable components ───────────

const Tab = ({ active, onClick, icon: Icon, label, count }) => (
  <button
    data-testid={`ig-funnel-tab-${label.toLowerCase()}`}
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
      active
        ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white shadow-lg'
        : 'text-gray-400 hover:text-white hover:bg-white/5'
    }`}
  >
    <Icon className="w-4 h-4 flex-shrink-0" />
    <span>{label}</span>
    {count != null && (
      <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? 'bg-white/20' : 'bg-white/10'}`}>
        {count}
      </span>
    )}
  </button>
);

const StatCard = ({ label, value, icon: Icon, color = 'purple' }) => {
  const colors = {
    purple: 'from-purple-500/10 to-purple-600/5 border-purple-500/20',
    pink: 'from-pink-500/10 to-pink-600/5 border-pink-500/20',
    green: 'from-green-500/10 to-green-600/5 border-green-500/20',
    amber: 'from-amber-500/10 to-amber-600/5 border-amber-500/20',
    blue: 'from-blue-500/10 to-blue-600/5 border-blue-500/20',
    red: 'from-red-500/10 to-red-600/5 border-red-500/20',
  };
  const iconColors = {
    purple: 'text-purple-400', pink: 'text-pink-400', green: 'text-green-400',
    amber: 'text-amber-400', blue: 'text-blue-400', red: 'text-red-400',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-4 flex items-center gap-4`}>
      <div className={`${iconColors[color]} bg-white/5 p-2.5 rounded-lg`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-white">{value ?? 0}</p>
        <p className="text-xs text-gray-400 mt-0.5">{label}</p>
      </div>
    </div>
  );
};

const ConfigBadge = ({ configured, label }) => (
  <div className="flex items-center justify-between py-3 px-4 bg-white/3 rounded-lg border border-white/5">
    <span className="text-sm text-gray-300">{label}</span>
    <div className="flex items-center gap-2">
      {configured ? (
        <>
          <CheckCircle className="w-4 h-4 text-green-400" />
          <span className="text-xs text-green-400 font-medium">Configured</span>
        </>
      ) : (
        <>
          <XCircle className="w-4 h-4 text-red-400" />
          <span className="text-xs text-red-400 font-medium">Not set</span>
        </>
      )}
    </div>
  </div>
);

const ScopeBadge = ({ scope }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium ${
    scope === 'specific_media'
      ? 'bg-blue-500/15 text-blue-400 border border-blue-500/20'
      : 'bg-purple-500/15 text-purple-400 border border-purple-500/20'
  }`}>
    {scope === 'specific_media' ? <Link className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
    {scope === 'specific_media' ? 'Specific Post' : 'Global'}
  </span>
);

const StateChip = ({ state }) => {
  const map = {
    initial: 'bg-gray-500/20 text-gray-400',
    awaiting_follow: 'bg-amber-500/20 text-amber-400',
    link_sent: 'bg-green-500/20 text-green-400',
  };
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${map[state] || 'bg-gray-500/20 text-gray-400'}`}>
      {state?.replace('_', ' ') || 'unknown'}
    </span>
  );
};

// ─────────── Keyword Modal ───────────

const defaultForm = () => ({
  keyword: '', scope: 'global', mediaId: '',
  isActive: true, publicReply: '@{username} Check your DM 👀',
  requireFollow: false, followRequiredMsg: '', followSuccessMsg: '',
  initialDm: '', destinationUrl: '',
});

function KeywordModal({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(editing ? { ...editing, mediaId: editing.mediaId || '' } : defaultForm());
  const [saving, setSaving] = useState(false);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.keyword.trim()) { toast.error('Keyword is required'); return; }
    if (form.scope === 'specific_media' && !form.mediaId.trim()) {
      toast.error('Media ID is required for Specific Post scope'); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, mediaId: form.scope === 'global' ? null : form.mediaId || null };
      if (editing) {
        await axios.put(`${API}/keywords/${editing.id}`, payload, { headers: ADMIN_HEADERS });
        toast.success('Keyword updated');
      } else {
        await axios.post(`${API}/keywords`, payload, { headers: ADMIN_HEADERS });
        toast.success('Keyword created');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save keyword');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[#1a1a1f] border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <h3 className="text-base font-semibold text-white">
            {editing ? 'Edit Keyword Rule' : 'New Keyword Rule'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Keyword */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Trigger Keyword *</label>
            <input
              data-testid="kw-keyword"
              value={form.keyword}
              onChange={e => set('keyword', e.target.value)}
              placeholder="e.g. website"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">Case-insensitive, whole-word match</p>
          </div>

          {/* Scope */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Scope</label>
            <select
              data-testid="kw-scope"
              value={form.scope}
              onChange={e => set('scope', e.target.value)}
              className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
            >
              <option value="global">Global — any post or reel</option>
              <option value="specific_media">Specific Post / Reel</option>
            </select>
          </div>

          {/* Media ID (conditional) */}
          {form.scope === 'specific_media' && (
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Instagram Media ID *</label>
              <input
                data-testid="kw-media-id"
                value={form.mediaId}
                onChange={e => set('mediaId', e.target.value)}
                placeholder="17855590893001122"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">Find in Meta Graph API explorer</p>
            </div>
          )}

          {/* Public Reply */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Public Comment Reply</label>
            <input
              data-testid="kw-public-reply"
              value={form.publicReply}
              onChange={e => set('publicReply', e.target.value)}
              placeholder="@{username} Check your DM 👀"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">Use {'{username}'} to mention the commenter</p>
          </div>

          {/* Initial DM */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Initial DM Message</label>
            <textarea
              data-testid="kw-initial-dm"
              value={form.initialDm}
              onChange={e => set('initialDm', e.target.value)}
              placeholder="Hi! Thanks for your interest. Here's what I have for you..."
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
            />
          </div>

          {/* Destination URL */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Destination URL</label>
            <input
              data-testid="kw-destination-url"
              value={form.destinationUrl}
              onChange={e => set('destinationUrl', e.target.value)}
              placeholder="https://edu.ruonn.com/course/..."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* Require Follow */}
          <div className="flex items-center justify-between py-2.5 px-3 bg-white/3 rounded-lg border border-white/5">
            <div>
              <p className="text-sm text-white font-medium">Require Follow</p>
              <p className="text-xs text-gray-500">User must follow your account to receive the link</p>
            </div>
            <button
              data-testid="kw-require-follow"
              onClick={() => set('requireFollow', !form.requireFollow)}
              className={`transition-colors ${form.requireFollow ? 'text-purple-400' : 'text-gray-600'}`}
            >
              {form.requireFollow ? <ToggleRight className="w-7 h-7" /> : <ToggleLeft className="w-7 h-7" />}
            </button>
          </div>

          {/* Follow messages (conditional) */}
          {form.requireFollow && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Follow Required Message</label>
                <input
                  value={form.followRequiredMsg}
                  onChange={e => set('followRequiredMsg', e.target.value)}
                  placeholder="Before I send you the link, make sure you are following me!"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Follow Success Message</label>
                <input
                  value={form.followSuccessMsg}
                  onChange={e => set('followSuccessMsg', e.target.value)}
                  placeholder="You're in! Here's your exclusive link:"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                />
              </div>
            </>
          )}

          {/* Active toggle */}
          <div className="flex items-center justify-between py-2.5 px-3 bg-white/3 rounded-lg border border-white/5">
            <p className="text-sm text-white font-medium">Active</p>
            <button
              data-testid="kw-is-active"
              onClick={() => set('isActive', !form.isActive)}
              className={`transition-colors ${form.isActive ? 'text-purple-400' : 'text-gray-600'}`}
            >
              {form.isActive ? <ToggleRight className="w-7 h-7" /> : <ToggleLeft className="w-7 h-7" />}
            </button>
          </div>
        </div>

        <div className="flex gap-3 p-5 border-t border-white/5">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm text-gray-400 border border-white/10 rounded-lg hover:bg-white/5 transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="kw-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2.5 text-sm font-medium bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {saving ? 'Saving...' : editing ? 'Update Rule' : 'Create Rule'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────── Main Page ───────────

export default function IGFunnelPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Overview
  const [status, setStatus] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  // Keywords
  const [keywords, setKeywords] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingKw, setEditingKw] = useState(null);
  const [kwFilter, setKwFilter] = useState('all');

  // Contacts
  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactPage, setContactPage] = useState(0);

  const PAGE_SIZE = 25;

  const loadStatus = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([
        axios.get(`${API}/status`, { headers: ADMIN_HEADERS }),
        axios.get(`${API}/analytics`, { headers: ADMIN_HEADERS }),
      ]);
      setStatus(s.data?.data);
      setAnalytics(a.data?.data);
    } catch { /* status load failure is non-critical */ }
  }, []);

  const loadKeywords = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/keywords/all`, { headers: ADMIN_HEADERS });
      setKeywords(data?.data || []);
    } catch { toast.error('Failed to load keywords'); }
    finally { setLoading(false); }
  }, []);

  const loadContacts = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/contacts?limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`,
        { headers: ADMIN_HEADERS },
      );
      setContacts(data?.data?.contacts || []);
      setContactsTotal(data?.data?.total || 0);
      setContactPage(page);
    } catch { toast.error('Failed to load contacts'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'overview') loadStatus();
    if (activeTab === 'keywords') loadKeywords();
    if (activeTab === 'leads') loadContacts(0);
    if (activeTab === 'settings') loadStatus();
  }, [activeTab, loadStatus, loadKeywords, loadContacts]);

  // Handle OAuth callback (Meta redirects back with ?oauth=success/error)
  useEffect(() => {
    const oauthResult = searchParams.get('oauth');
    const igUsername = searchParams.get('ig_username');
    if (oauthResult === 'success') {
      toast.success(
        igUsername ? `Instagram @${igUsername} connected!` : 'Instagram account connected!'
      );
      setActiveTab('settings');
      loadStatus();
      setSearchParams({}, { replace: true }); // clean URL
    } else if (oauthResult === 'error') {
      const msg = searchParams.get('msg');
      const readable = {
        denied: 'You cancelled the Instagram connection.',
        meta_not_configured: 'Meta credentials not configured on server.',
        unauthorized: 'Admin authentication failed.',
      }[msg] || 'Instagram connection failed. Please try again.';
      toast.error(readable);
      setActiveTab('settings');
      setSearchParams({}, { replace: true });
    }
  }, []); // run once on mount

  const handleToggleKw = async (kw) => {
    try {
      await axios.patch(`${API}/keywords/${kw.id}/toggle`, {}, { headers: ADMIN_HEADERS });
      toast.success(`Keyword "${kw.keyword}" ${kw.isActive ? 'deactivated' : 'activated'}`);
      loadKeywords();
    } catch { toast.error('Failed to toggle keyword'); }
  };

  const handleDeleteKw = async (kw) => {
    if (!window.confirm(`Delete keyword "${kw.keyword}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/keywords/${kw.id}`, { headers: ADMIN_HEADERS });
      toast.success('Keyword deleted');
      loadKeywords();
    } catch { toast.error('Failed to delete keyword'); }
  };

  const handleExportCSV = () => {
    const url = `${API}/contacts/export`;
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'ig_funnel_contacts.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredKeywords = kwFilter === 'all' ? keywords
    : kwFilter === 'active' ? keywords.filter(k => k.isActive)
    : keywords.filter(k => !k.isActive);

  const totalPages = Math.ceil(contactsTotal / PAGE_SIZE);

  return (
    <div className="min-h-screen bg-[#0d0d10] text-white">
      {/* Header */}
      <div className="border-b border-white/5 bg-[#111115]">
        <div className="px-6 py-5">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-gradient-to-br from-pink-500/20 to-purple-600/20 rounded-lg border border-pink-500/20">
              <Instagram className="w-5 h-5 text-pink-400" />
            </div>
            <h1 className="text-xl font-semibold text-white">IG Funnel</h1>
          </div>
          <p className="text-sm text-gray-500 ml-12">
            Instagram keyword automation — comment triggers, DMs, follow gates, link delivery
          </p>
        </div>

        {/* Tabs */}
        <div className="px-6 pb-0 flex items-center gap-1 overflow-x-auto scrollbar-hide">
          <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} icon={BarChart2} label="Overview" />
          <Tab active={activeTab === 'keywords'} onClick={() => setActiveTab('keywords')} icon={Hash} label="Keywords" count={keywords.length || undefined} />
          <Tab active={activeTab === 'leads'} onClick={() => setActiveTab('leads')} icon={Users} label="Leads" count={contactsTotal || undefined} />
          <Tab active={activeTab === 'settings'} onClick={() => setActiveTab('settings')} icon={Settings} label="Settings" />
        </div>
      </div>

      {/* Content */}
      <div className="p-6">

        {/* ── OVERVIEW ── */}
        {activeTab === 'overview' && (
          <div data-testid="ig-funnel-overview" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-gray-400">Last 30 days</h2>
              <button onClick={loadStatus} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors">
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </button>
            </div>

            {/* Analytics stats */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              <StatCard label="Comments Received" value={analytics?.commentsReceived} icon={MessageSquare} color="blue" />
              <StatCard label="Keywords Matched" value={analytics?.keywordsMatched} icon={Hash} color="purple" />
              <StatCard label="Public Replies Sent" value={analytics?.publicRepliesSent} icon={ChevronRight} color="pink" />
              <StatCard label="DMs Sent" value={analytics?.dmsSent} icon={MessageSquare} color="purple" />
              <StatCard label="Links Delivered" value={analytics?.linksDelivered} icon={Link} color="green" />
              <StatCard label="Follows Verified" value={analytics?.followsVerified} icon={UserCheck} color="amber" />
            </div>

            {/* Queue status */}
            {status?.queue && (
              <div>
                <h3 className="text-xs font-medium text-gray-400 mb-3">Job Queue Status</h3>
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { label: 'Pending', val: status.queue.pending, color: 'amber' },
                    { label: 'Processing', val: status.queue.processing, color: 'blue' },
                    { label: 'Done', val: status.queue.done, color: 'green' },
                    { label: 'Failed', val: status.queue.failed, color: 'red' },
                  ].map(({ label, val, color }) => (
                    <StatCard key={label} label={label} value={val} icon={Zap} color={color} />
                  ))}
                </div>
              </div>
            )}

            {/* Diagnostic alert when no webhook events received */}
            {status?.webhookDiagnostics?.totalReceived === 0 && status?.fullyConfigured && (
              <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-400">No webhook events received yet</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Meta is not delivering events. Most likely cause: the <strong>"comments"</strong> field is not subscribed in your Meta Developer Console webhook settings. Go to Settings → to see the full checklist.
                  </p>
                </div>
              </div>
            )}

            {/* Signature issue alert */}
            {status?.webhookDiagnostics?.signatureIssue && (
              <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-400">All webhook events are being rejected</p>
                  <p className="text-xs text-gray-400 mt-1">
                    META_APP_SECRET in production .env doesn't match Meta app. Check Settings → for details.
                  </p>
                </div>
              </div>
            )}

            {/* Active keywords summary */}
            <div className="bg-white/3 border border-white/5 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-white">Keyword Rules</h3>
                <button
                  onClick={() => setActiveTab('keywords')}
                  className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
                >
                  Manage →
                </button>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-400">Active: <span className="text-green-400 font-medium">{status?.activeKeywords ?? 0}</span></span>
                <span className="text-gray-400">Total: <span className="text-white font-medium">{status?.keywordCount ?? 0}</span></span>
                <span className="text-gray-400">Leads: <span className="text-pink-400 font-medium">{status?.contactCount ?? 0}</span></span>
              </div>
            </div>
          </div>
        )}

        {/* ── KEYWORDS ── */}
        {activeTab === 'keywords' && (
          <div data-testid="ig-funnel-keywords" className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {['all', 'active', 'inactive'].map(f => (
                  <button
                    key={f}
                    onClick={() => setKwFilter(f)}
                    className={`px-3 py-1.5 text-xs rounded-lg transition-colors capitalize ${
                      kwFilter === f ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {f} ({
                      f === 'all' ? keywords.length
                        : f === 'active' ? keywords.filter(k => k.isActive).length
                        : keywords.filter(k => !k.isActive).length
                    })
                  </button>
                ))}
              </div>
              <button
                data-testid="ig-funnel-new-keyword-btn"
                onClick={() => { setEditingKw(null); setShowModal(true); }}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity"
              >
                <Plus className="w-4 h-4" /> New Keyword
              </button>
            </div>

            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading...</div>
            ) : filteredKeywords.length === 0 ? (
              <div className="text-center py-16 bg-white/3 border border-white/5 rounded-xl">
                <Hash className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">No keyword rules yet</p>
                <p className="text-gray-600 text-xs mt-1">Create your first keyword to start automating Instagram comments</p>
                <button
                  onClick={() => { setEditingKw(null); setShowModal(true); }}
                  className="mt-4 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-500 transition-colors"
                >
                  Create first keyword
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredKeywords.map(kw => (
                  <div
                    key={kw.id}
                    data-testid={`keyword-row-${kw.id}`}
                    className="bg-white/3 border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="font-mono text-base font-semibold text-white bg-white/5 px-2 py-0.5 rounded">
                            {kw.keyword}
                          </span>
                          <ScopeBadge scope={kw.scope} />
                          <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                            kw.isActive
                              ? 'bg-green-500/15 text-green-400 border border-green-500/20'
                              : 'bg-gray-500/15 text-gray-400 border border-gray-500/20'
                          }`}>
                            {kw.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-500">
                          {kw.mediaId && (
                            <span>Media ID: <span className="text-gray-400 font-mono">{kw.mediaId}</span></span>
                          )}
                          {kw.publicReply && (
                            <span>Reply: <span className="text-gray-400 italic">"{kw.publicReply.slice(0, 40)}{kw.publicReply.length > 40 ? '…' : ''}"</span></span>
                          )}
                          {kw.requireFollow && (
                            <span className="text-amber-500">Requires follow verification</span>
                          )}
                          {kw.destinationUrl && (
                            <span>URL: <span className="text-purple-400">{kw.destinationUrl.slice(0, 40)}{kw.destinationUrl.length > 40 ? '…' : ''}</span></span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          data-testid={`toggle-kw-${kw.id}`}
                          onClick={() => handleToggleKw(kw)}
                          title={kw.isActive ? 'Deactivate' : 'Activate'}
                          className={`p-2 rounded-lg transition-colors ${
                            kw.isActive ? 'text-green-400 hover:bg-green-500/10' : 'text-gray-500 hover:bg-white/5'
                          }`}
                        >
                          {kw.isActive ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                        </button>
                        <button
                          data-testid={`edit-kw-${kw.id}`}
                          onClick={() => { setEditingKw(kw); setShowModal(true); }}
                          className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`delete-kw-${kw.id}`}
                          onClick={() => handleDeleteKw(kw)}
                          className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── LEADS ── */}
        {activeTab === 'leads' && (
          <div data-testid="ig-funnel-leads" className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-400">
                {contactsTotal} total leads
              </p>
              <button
                data-testid="ig-funnel-export-csv"
                onClick={handleExportCSV}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-white/10 text-gray-300 rounded-lg hover:bg-white/5 transition-colors"
              >
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>

            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading...</div>
            ) : contacts.length === 0 ? (
              <div className="text-center py-16 bg-white/3 border border-white/5 rounded-xl">
                <Users className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">No leads yet</p>
                <p className="text-gray-600 text-xs mt-1">Leads appear when someone submits their email through the funnel page</p>
              </div>
            ) : (
              <>
                <div className="bg-white/3 border border-white/5 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/5">
                          <th className="text-left px-4 py-3 text-xs font-medium text-gray-400">Email</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-gray-400">Source (ref)</th>
                          <th className="text-center px-4 py-3 text-xs font-medium text-gray-400">Verified</th>
                          <th className="text-right px-4 py-3 text-xs font-medium text-gray-400">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contacts.map((c, i) => (
                          <tr
                            key={c.id || i}
                            data-testid={`contact-row-${c.id}`}
                            className="border-b border-white/3 hover:bg-white/3 transition-colors"
                          >
                            <td className="px-4 py-3 text-white font-medium">{c.email}</td>
                            <td className="px-4 py-3">
                              <span className="font-mono text-xs bg-white/5 px-1.5 py-0.5 rounded text-gray-300">
                                {c.ref || '—'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {c.emailVerified
                                ? <CheckCircle className="w-4 h-4 text-green-400 mx-auto" />
                                : <XCircle className="w-4 h-4 text-gray-600 mx-auto" />}
                            </td>
                            <td className="px-4 py-3 text-right text-xs text-gray-500">
                              {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between text-sm text-gray-400">
                    <span>Page {contactPage + 1} of {totalPages}</span>
                    <div className="flex gap-2">
                      <button
                        disabled={contactPage === 0}
                        onClick={() => loadContacts(contactPage - 1)}
                        className="px-3 py-1.5 border border-white/10 rounded-lg hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>
                      <button
                        disabled={contactPage + 1 >= totalPages}
                        onClick={() => loadContacts(contactPage + 1)}
                        className="px-3 py-1.5 border border-white/10 rounded-lg hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── SETTINGS ── */}
        {activeTab === 'settings' && (
          <div data-testid="ig-funnel-settings" className="max-w-2xl space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-gray-400">Meta Connection Status</h2>
              <button onClick={loadStatus} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors">
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </button>
            </div>

            {/* ── CONNECT INSTAGRAM BUTTON ── */}
            <div className="bg-gradient-to-br from-pink-500/8 to-purple-600/8 border border-pink-500/15 rounded-2xl p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-1">
                    <Instagram className="w-4 h-4 text-pink-400" />
                    Connect Your Instagram Account
                  </h3>
                  <p className="text-xs text-gray-400 leading-relaxed max-w-sm">
                    Authorize Ruonn to reply to comments and send DMs on your behalf.
                    Credentials are stored server-side only — never exposed to the browser.
                  </p>
                </div>
                <a
                  data-testid="connect-instagram-btn"
                  href={`${BACKEND_URL}/api/ig-funnel/auth/instagram/start`}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-pink-600 to-purple-600 text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity whitespace-nowrap flex-shrink-0"
                >
                  <ExternalLink className="w-4 h-4" />
                  {status?.accessTokenConfigured ? 'Reconnect Instagram' : 'Connect Instagram'}
                </a>
              </div>

              {/* Connected account badge */}
              {status?.igAccountId && (
                <div className="mt-4 pt-4 border-t border-white/5 flex items-center gap-2 text-xs text-green-400">
                  <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                  <span>
                    Connected — IG Account ID: <code className="text-white font-mono">{status.igAccountId}</code>
                    {status.accessTokenConfigured && (
                      <span className="text-gray-500 ml-1">(token active)</span>
                    )}
                  </span>
                </div>
              )}

              <div className="mt-3 text-xs text-gray-500 flex items-start gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-gray-600 flex-shrink-0 mt-0.5" />
                <span>
                  Redirect URL to enter in Meta: <code className="text-purple-300 select-all">{BACKEND_URL}/api/ig-funnel/auth/instagram/callback</code>
                </span>
              </div>
            </div>

            {/* Overall status banner */}
            {status && (
              <div className={`flex items-center gap-3 p-4 rounded-xl border ${
                status.fullyConfigured
                  ? 'bg-green-500/10 border-green-500/20'
                  : 'bg-amber-500/10 border-amber-500/20'
              }`}>
                {status.fullyConfigured
                  ? <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  : <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                }
                <div>
                  <p className={`text-sm font-medium ${status.fullyConfigured ? 'text-green-400' : 'text-amber-400'}`}>
                    {status.fullyConfigured ? 'Fully configured' : 'Configuration incomplete'}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {status.fullyConfigured
                      ? 'All Meta credentials are set. Ready for Instagram automation.'
                      : 'Add missing environment variables to your .env file to activate automation.'}
                  </p>
                </div>
              </div>
            )}

            {/* Config items */}
            <div className="space-y-2">
              <ConfigBadge configured={status?.metaAppConfigured} label="Meta App ID" />
              <ConfigBadge configured={status?.appSecretConfigured} label="Meta App Secret (env only — never shown)" />
              <ConfigBadge configured={status?.accessTokenConfigured} label="Meta Access Token (env only — never shown)" />
              <ConfigBadge configured={status?.verifyTokenConfigured} label="Webhook Verify Token (env only — never shown)" />
              <ConfigBadge configured={status?.igAccountConfigured} label="Instagram Business Account ID" />
            </div>

            {/* Account info (safe) */}
            {(status?.metaAppId || status?.igAccountId) && (
              <div className="bg-white/3 border border-white/5 rounded-xl p-4 space-y-2">
                <h3 className="text-xs font-medium text-gray-400 mb-2">Account Identifiers</h3>
                {status.metaAppId && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Meta App ID</span>
                    <span className="text-white font-mono text-xs">{status.metaAppId}</span>
                  </div>
                )}
                {status.igAccountId && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">IG Account ID</span>
                    <span className="text-white font-mono text-xs">{status.igAccountId}</span>
                  </div>
                )}
              </div>
            )}

            {/* ── DIAGNOSTICS ── */}
            <div className="border-t border-white/5 pt-4">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-400" />
                Webhook Diagnostics
              </h3>

              {status?.webhookDiagnostics && (
                <div className="space-y-3">
                  {/* Webhook counters */}
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: 'Received', val: status.webhookDiagnostics.totalReceived, color: 'text-blue-400' },
                      { label: 'Accepted', val: status.webhookDiagnostics.totalReceived - status.webhookDiagnostics.totalRejected, color: 'text-green-400' },
                      { label: 'Rejected', val: status.webhookDiagnostics.totalRejected, color: 'text-red-400' },
                    ].map(({ label, val, color }) => (
                      <div key={label} className="bg-white/3 border border-white/5 rounded-xl p-3 text-center">
                        <p className={`text-xl font-bold ${color}`}>{val ?? 0}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Signature mismatch alert — show specific reason */}
                  {status.webhookDiagnostics.signatureIssue && (() => {
                    const f = status.webhookDiagnostics.lastSigFailure;
                    const reason = f?.reason || '';

                    const messages = {
                      app_secret_not_set: {
                        title: 'META_APP_SECRET is not set in the running container',
                        body: 'The env var is empty. Run: docker compose exec ruonn-backend env | grep META_APP_SECRET — if blank, your docker-compose.yml is not forwarding it from .env.',
                      },
                      header_missing: {
                        title: 'X-Hub-Signature-256 header is not reaching the backend',
                        body: 'Meta is sending the signature but Nginx is stripping it. Deploy the latest nginx.conf (now has proxy_pass_header X-Hub-Signature-256;) then reload Nginx.',
                      },
                    };

                    const isWhitespace = f?.appSecretHasWhitespace;
                    const isHmacMismatch = reason.startsWith('hmac_mismatch');
                    const fallbackMsg = isWhitespace
                      ? { title: 'META_APP_SECRET has extra whitespace', body: 'Your .env value has a space or newline. Open the file, check nothing is before/after the value, rebuild the container.' }
                      : isHmacMismatch
                      ? { title: `META_APP_SECRET value is wrong (${f?.appSecretLength || 0} chars in env)`, body: 'The secret doesn\'t match what Meta uses. IMPORTANT: META_APP_SECRET must be the App Secret from Meta → Settings → Basic, NOT the Verify Token you created for the webhook.' }
                      : { title: 'Signature validation failing', body: 'Trigger one more webhook event (comment on Reel), then refresh this page to see the specific reason.' };

                    const { title, body: msg } = messages[reason] || fallbackMsg;

                    return (
                      <div className="flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                        <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-red-400">{title}</p>
                          <p className="text-xs text-gray-400 mt-1">{msg}</p>
                          {f && (
                            <div className="mt-2 p-2.5 bg-black/20 rounded-lg text-xs space-y-1.5">
                              {f.reason && <div className="flex gap-2"><span className="text-gray-500 w-28 flex-shrink-0">Reason:</span><code className="text-red-300">{f.reason}</code></div>}
                              {f.headerPresent === false && <div className="flex gap-2"><span className="text-gray-500 w-28 flex-shrink-0">Sig header:</span><span className="text-amber-400">Not received — Nginx blocking it</span></div>}
                              {f.headerPresent === true && <div className="flex gap-2"><span className="text-gray-500 w-28 flex-shrink-0">Sig header:</span><span className="text-green-400">Present ✓</span><code className="text-gray-400">{f.headerPrefix}</code></div>}
                              {f.appSecretLength !== undefined && <div className="flex gap-2"><span className="text-gray-500 w-28 flex-shrink-0">Secret length:</span><span className={f.appSecretLength === 32 ? 'text-green-400' : 'text-amber-400'}>{f.appSecretLength} chars {f.appSecretLength !== 32 ? '⚠ should be 32' : '✓'}</span></div>}
                              {f.appSecretHasWhitespace && <div className="flex gap-2"><span className="text-gray-500 w-28 flex-shrink-0">Whitespace:</span><span className="text-amber-400">Detected in secret</span></div>}
                              {f.bodyLength !== undefined && <div className="flex gap-2"><span className="text-gray-500 w-28 flex-shrink-0">Body size:</span><span className="text-gray-300">{f.bodyLength} bytes</span></div>}
                            </div>
                          )}
                          {/* Run this on VPS */}
                          <div className="mt-3 p-2.5 bg-black/30 rounded-lg">
                            <p className="text-xs text-gray-500 mb-1">Run on your VPS to verify the container sees the secret:</p>
                            <code className="text-xs text-blue-300 break-all select-all">{'docker compose exec ruonn-backend python3 -c "import os; s=os.environ.get(\'META_APP_SECRET\',\'\'); print(\'len=\'+str(len(s))+\' has_whitespace=\'+str(s!=s.strip()))"'}</code>
                          </div>
                          <p className="text-xs text-amber-400 mt-2 font-medium">
                            Common mistake: META_APP_SECRET is the 32-char hex string from Meta → Settings → Basic → App Secret — NOT the Verify Token you set up for the webhook.
                          </p>
                        </div>
                      </div>
                    );
                  })()}

                  {/* No events received yet */}
                  {status.webhookDiagnostics.totalReceived === 0 && (
                    <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                      <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-400">No webhook events received yet</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Meta has not delivered any events to this server. Check that:
                        </p>
                        <ul className="text-xs text-gray-400 mt-1.5 space-y-1 list-disc list-inside">
                          <li>Webhook URL is set to <code className="text-amber-300">https://api.ruonn.com/api/ig-funnel/webhook</code></li>
                          <li>In Meta Developer Console → Webhooks → <strong>the "comments" field is subscribed</strong> (this is separate from verification)</li>
                          <li>App is in Development mode and tester accounts are added</li>
                          <li>The Instagram Business Account is connected to the app</li>
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Last received */}
                  {status.webhookDiagnostics.lastReceivedAt && (
                    <div className="flex items-center justify-between text-sm px-3 py-2.5 bg-white/3 rounded-lg">
                      <span className="text-gray-400 flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" /> Last webhook received
                      </span>
                      <span className="text-gray-300 text-xs">
                        {new Date(status.webhookDiagnostics.lastReceivedAt).toLocaleString()}
                      </span>
                    </div>
                  )}

                  {/* Raw events stored */}
                  <div className="flex items-center justify-between text-sm px-3 py-2.5 bg-white/3 rounded-lg">
                    <span className="text-gray-400">Raw events stored (passed signature)</span>
                    <span className="text-white font-medium">{status.webhookDiagnostics.rawEventsStored ?? 0}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Last API error */}
            {status?.lastApiError && (
              <div className="bg-red-500/5 border border-red-500/15 rounded-xl p-4">
                <h3 className="text-sm font-medium text-red-400 mb-2 flex items-center gap-1.5">
                  <XCircle className="w-4 h-4" /> Last Meta API Error
                </h3>
                <div className="space-y-1.5 text-xs">
                  <div className="flex gap-2">
                    <span className="text-gray-500 w-14 flex-shrink-0">Action:</span>
                    <span className="text-gray-300 font-mono">{status.lastApiError.action}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-gray-500 w-14 flex-shrink-0">Time:</span>
                    <span className="text-gray-300">{status.lastApiError.ts ? new Date(status.lastApiError.ts).toLocaleString() : '—'}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-gray-500 w-14 flex-shrink-0">Error:</span>
                    <span className="text-red-300 font-mono break-all">{status.lastApiError.error}</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Common causes: expired access token, missing permissions (instagram_manage_comments, instagram_manage_messages), app not approved by Meta.
                </p>
              </div>
            )}

            {/* Recent failed jobs */}
            {status?.recentFailedJobs?.length > 0 && (
              <div className="bg-white/3 border border-white/5 rounded-xl p-4">
                <h3 className="text-xs font-medium text-gray-400 mb-3">Recent Failed Jobs</h3>
                <div className="space-y-2">
                  {status.recentFailedJobs.map((j, i) => (
                    <div key={j.id || i} className="text-xs bg-black/20 rounded-lg p-2.5">
                      <div className="flex justify-between mb-1">
                        <span className="text-amber-400 font-medium">{j.type}</span>
                        <span className="text-gray-500">{j.updatedAt ? new Date(j.updatedAt).toLocaleString() : ''}</span>
                      </div>
                      <p className="text-red-300 font-mono break-all">{j.error || 'Unknown error'}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Queue status */}
            {status?.queue && (
              <div className="bg-white/3 border border-white/5 rounded-xl p-4">
                <h3 className="text-xs font-medium text-gray-400 mb-3">Queue Status</h3>
                <div className="grid grid-cols-4 gap-2 text-center">
                  {[
                    { label: 'Pending', val: status.queue.pending, col: 'text-amber-400' },
                    { label: 'Processing', val: status.queue.processing, col: 'text-blue-400' },
                    { label: 'Done', val: status.queue.done, col: 'text-green-400' },
                    { label: 'Failed', val: status.queue.failed, col: 'text-red-400' },
                  ].map(({ label, val, col }) => (
                    <div key={label}>
                      <p className={`text-lg font-bold ${col}`}>{val}</p>
                      <p className="text-xs text-gray-500">{label}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Meta webhook setup checklist */}
            <div className="bg-blue-500/5 border border-blue-500/15 rounded-xl p-4">
              <h3 className="text-sm font-medium text-blue-400 mb-3">Meta Webhook Setup Checklist</h3>
              <div className="space-y-2 text-xs text-gray-400">
                {[
                  { step: '1', text: 'Webhook URL set to https://api.ruonn.com/api/ig-funnel/webhook' },
                  { step: '2', text: 'Verify Token verified (GET challenge passed)' },
                  { step: '3', text: 'In Meta Developer Console → Webhooks → Subscribe to "comments" field (required for comment triggers)' },
                  { step: '4', text: 'In Meta Developer Console → Webhooks → Subscribe to "messages" field (required for DMs)' },
                  { step: '5', text: 'App permissions: instagram_manage_comments, instagram_manage_messages, pages_manage_engagement' },
                  { step: '6', text: 'Tester accounts added under App Roles → Testers in Meta Developer Console' },
                  { step: '7', text: 'Instagram Business Account linked to the app' },
                ].map(({ step, text }) => (
                  <div key={step} className="flex gap-2.5">
                    <span className="text-blue-500 font-bold flex-shrink-0">{step}.</span>
                    <span>{text}</span>
                  </div>
                ))}
              </div>
              {status && !status.fullyConfigured && (
                <div className="mt-3 pt-3 border-t border-white/5">
                  <p className="text-xs text-gray-400 mb-2">Missing env variables — add to production <code className="text-blue-300">backend/.env</code>:</p>
                  <pre className="text-xs text-gray-300 bg-black/30 rounded-lg p-3 font-mono leading-relaxed overflow-x-auto">
{`META_APP_ID=your_meta_app_id
META_APP_SECRET=your_meta_app_secret
META_ACCESS_TOKEN=your_page_access_token
META_VERIFY_TOKEN=your_chosen_verify_token
META_IG_ACCOUNT_ID=your_ig_account_id`}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Keyword Modal */}
      {showModal && (
        <KeywordModal
          editing={editingKw}
          onClose={() => { setShowModal(false); setEditingKw(null); }}
          onSaved={loadKeywords}
        />
      )}
    </div>
  );
}
